/**
 * An owner's name, rendered as a link into their apartment.
 *
 * Ownership is the EFFECTIVE lessor — COALESCE(apartments.lessor_id,
 * properties.lessor_id) — the same rule the invoice generator bills by.
 *
 *   • one apartment    → navigates straight to its detail page
 *   • several          → a picker dialog asks which one, then navigates
 *   • none             → falls back to the owner's profile on the Owners page
 *   • no lessor at all → plain text
 *
 * Shared by every surface that shows an owner name (Payments invoices, the
 * owner detail modal) so the destination never depends on where you clicked.
 */
import { useState, type MouseEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Building2, ChevronRight } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useApartmentsByLessor } from "@/hooks/v2/useLessors";

export function OwnerApartmentLink({
  lessorId,
  name,
  className,
  chevron = false,
  onNavigate,
  propertyIds,
}: {
  lessorId: string | null | undefined;
  name: string;
  className?: string;
  /** Show a trailing chevron affordance next to the name. */
  chevron?: boolean;
  /** Fired just before navigating — e.g. to close the modal we're inside. */
  onNavigate?: () => void;
  /**
   * Narrow the candidates to apartments inside these properties — e.g. the
   * ones an invoice bills for. Ignored when the owner holds nothing there, so
   * a stale or property-less invoice still links somewhere useful.
   */
  propertyIds?: number[];
}) {
  const navigate = useNavigate();
  const { data: apartmentsByLessor = {} } = useApartmentsByLessor();
  const [pickerOpen, setPickerOpen] = useState(false);

  const all = lessorId ? (apartmentsByLessor[lessorId] ?? []) : [];
  // Prefer the apartments inside the properties in question; fall back to the
  // whole portfolio when the owner holds none of them.
  const scoped =
    propertyIds && propertyIds.length > 0
      ? all.filter((a) => propertyIds.includes(Number(a.property_id)))
      : [];
  const apartments = scoped.length > 0 ? scoped : all;
  const body = (
    <>
      {name}
      {chevron && (
        <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-primary" />
      )}
    </>
  );

  if (!lessorId) return <span className={className}>{name}</span>;

  // Several apartments — there's no single right answer, so ask.
  if (apartments.length > 1) {
    return (
      <>
        <button
          type="button"
          onClick={(e: MouseEvent) => {
            e.stopPropagation();
            e.preventDefault();
            setPickerOpen(true);
          }}
          title={`Choose one of ${apartments.length} apartments`}
          className={className}
        >
          {body}
        </button>

        <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Open which apartment?</DialogTitle>
              <DialogDescription>
                {name} holds {apartments.length} apartments. Pick the one to
                open.
              </DialogDescription>
            </DialogHeader>
            <div className="max-h-[60vh] space-y-2 overflow-y-auto">
              {apartments.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => {
                    // Route first, then tear the dialogs down — the close
                    // handler can unmount this component's owner.
                    navigate(
                      `/admin/properties/${a.property_id}/apartments/${a.id}`
                    );
                    setPickerOpen(false);
                    onNavigate?.();
                  }}
                  className="group flex w-full items-center gap-3 rounded-xl border bg-card px-3 py-2.5 text-left transition hover:border-primary/40 hover:bg-muted/50"
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                    <Building2 className="h-4 w-4" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium">
                      {a.label ?? "Apartment"}
                    </span>
                    <span className="block truncate text-xs text-muted-foreground">
                      {a.property_title ?? "—"}
                    </span>
                  </span>
                  <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-primary" />
                </button>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  const only = apartments[0] ?? null;
  return (
    <Link
      to={
        only
          ? `/admin/properties/${only.property_id}/apartments/${only.id}`
          : `/admin/owners?owner=${lessorId}`
      }
      onClick={(e: MouseEvent) => {
        e.stopPropagation();
        onNavigate?.();
      }}
      title={
        only
          ? `Open apartment${only.label ? ` · ${only.label}` : ""}`
          : "Open owner details"
      }
      className={className}
    >
      {body}
    </Link>
  );
}
